import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { publicRegistry } from '@/i18n/dictionaries/publicRegistry';
import { useTranslation } from '@/i18n/useTranslation';
import { RESOURCE_TYPE_LABEL_KEY, RESOURCE_TYPES } from '@/features/common/resourceType';
import { domainLabel, useThematicDomains } from '@/features/common/useThematicDomains';
import { draftFrom, FILTER_KEYS, SOURCE_KEY } from '@/features/publicsite/registry/libraryQuery';
import type { LibraryFilterDraft } from '@/features/publicsite/types/registry/LibraryFilterDraft';
import '@/features/publicsite/registry/library-filters.css';

/**
 * Advanced search over the digital library (FR-021).
 *
 * The requirement lists what a reader must be able to narrow by — "tematică, autori,
 * cuvinte-cheie, an de publicare, instituție sau tip de document" — and the page offered a single
 * free-text box. The backend was answering author, year and type all along; nothing in the
 * interface ever sent them, so the filters existed and were unreachable.
 *
 * Everything is written to the URL rather than held in component state. That is what makes a
 * filtered list shareable and the back button meaningful, and it is the convention the other
 * registries on this site already follow with their `q`.
 */
export function LibraryFilters() {
  const { t, language } = useTranslation(publicRegistry);
  // FR-021 names "tematică" first among the things a reader can filter by. The backend answered
  // ?domain= from the start; nothing offered it, and nothing could — the vocabulary lives in a
  // nomenclature the anonymous page was not allowed to read until now.
  const domains = useThematicDomains();
  const [params, setParams] = useSearchParams();
  const [draft, setDraft] = useState<LibraryFilterDraft>(() => draftFrom(params));

  // Follows the URL rather than owning the value, so a shared link and the back button both show
  // the filters that produced the list below.
  useEffect(() => setDraft(draftFrom(params)), [params]);

  const activeFilters = [...FILTER_KEYS, SOURCE_KEY].filter((k) => params.get(k));
  const set = (key: keyof LibraryFilterDraft, value: string) =>
    setDraft((d) => ({ ...d, [key]: value }));

  return (
    <form
      className="card library-filters"
      role="search"
      onSubmit={(e) => {
        e.preventDefault();
        // Empty fields are left out entirely rather than sent blank: the URL stays readable, and
        // the backend treats a missing parameter as "no filter" while an empty string is a value.
        setParams(Object.fromEntries(Object.entries(draft).filter(([, v]) => v.trim())));
      }}
    >
      <div className="library-filters__query">
        <label className="visually-hidden" htmlFor="q">
          {t('librarySearchLabel')}
        </label>
        <input
          id="q"
          name="q"
          type="search"
          value={draft.q}
          onChange={(e) => set('q', e.target.value)}
          placeholder={t('librarySearchPlaceholder')}
        />
        <button className="btn btn--primary" type="submit">
          {t('libraryFilterApply')}
        </button>
        <Link className="btn btn--ghost" to="/biblioteca">
          {t('servicesReset')}
        </Link>
      </div>

      {/* Open when something is filtering, so a shared link shows why the list is short rather
          than hiding the reason behind a closed panel. */}
      <details className="library-filters__advanced" open={activeFilters.length > 0}>
        <summary>{t('libraryFiltersToggle')}</summary>

        <div className="library-filters__grid">
          <div className="field">
            <label htmlFor="library-source">{t('librarySourceLabel')}</label>
            <select
              id="library-source"
              name="source"
              value={draft.source}
              onChange={(e) => set('source', e.target.value)}
            >
              <option value="">{t('libraryFilterAny')}</option>
              <option value="national">{t('librarySourceNational')}</option>
              <option value="international">{t('librarySourceInternational')}</option>
            </select>
          </div>

          <div className="field">
            <label htmlFor="library-author">{t('libraryFilterAuthor')}</label>
            <input
              id="library-author"
              name="author"
              type="text"
              value={draft.author}
              onChange={(e) => set('author', e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="library-keyword">{t('libraryFilterKeyword')}</label>
            <input
              id="library-keyword"
              name="keyword"
              type="text"
              value={draft.keyword}
              onChange={(e) => set('keyword', e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="library-institution">{t('libraryFilterInstitution')}</label>
            <input
              id="library-institution"
              name="institution"
              type="text"
              value={draft.institution}
              onChange={(e) => set('institution', e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="library-year">{t('libraryFilterYear')}</label>
            <input
              id="library-year"
              name="year"
              type="number"
              inputMode="numeric"
              min={1900}
              max={new Date().getFullYear() + 1}
              value={draft.year}
              onChange={(e) => set('year', e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="library-domain">{t('libraryFilterDomain')}</label>
            <select
              id="library-domain"
              name="domain"
              value={draft.domain}
              onChange={(e) => set('domain', e.target.value)}
            >
              <option value="">{t('libraryFilterAny')}</option>
              {domains.map((d) => (
                <option key={d.id} value={d.id}>
                  {domainLabel(d, language)}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label htmlFor="library-type">{t('libraryFilterType')}</label>
            <select
              id="library-type"
              name="type"
              value={draft.type}
              onChange={(e) => set('type', e.target.value)}
            >
              <option value="">{t('libraryFilterAny')}</option>
              {RESOURCE_TYPES.map((type) => (
                <option key={type} value={type}>
                  {t(RESOURCE_TYPE_LABEL_KEY[type])}
                </option>
              ))}
            </select>
          </div>
        </div>
      </details>
    </form>
  );
}
